/**
 * TestScenarioRunner - Simulerar fullständiga revisioner för testlab
 * 
 * Ansvarig för:
 * - Simulering av 3 revisioner samtidigt
 * - Generering av testdata för olika scenarion
 * - Monitoring av progress och kostnader
 * - Dummy PDF/HTML rapport-preview
 */

import { supabase } from '@/integrations/supabase/client';
import { RevisionOrchestrator } from './RevisionOrchestrator';
import { MiniAnalysisWorker } from './MiniAnalysisWorker';
import { PremiumReportWorker } from './PremiumReportWorker';
import { TestScenario, RevisionStatus } from './types';
import { ParsedCSVData } from '@/lib/csv-parser';
import { makeBatches } from '@/lib/utils/tokens';
import { MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM } from './config';

export class TestScenarioRunner {
  private orchestrators: Map<string, RevisionOrchestrator> = new Map();
  public workers: MiniAnalysisWorker[] = []; // Public för verbose toggle
  private reportWorker: PremiumReportWorker;
  private isRunning = false;

  constructor() {
    // Endast 1 worker för att undvika rate limits och connection issues
    this.workers.push(new MiniAnalysisWorker('main-worker'));
    this.reportWorker = new PremiumReportWorker('report-worker');
  }

  /**
   * Säkerställ att workers kör i bakgrunden utan att starta nya revisioner
   * Auto-stoppar workers efter 30s om inga jobb finns
   */
  public async ensureWorkersRunning(): Promise<void> {
    if (!this.isRunning) {
      console.log('🤖 ensureWorkersRunning(): starting background workers');
      this.isRunning = true;
      await this.startWorkers();
      
      // Auto-stop endast om det faktiskt är idle (inga pending/processing-jobb och inga aktiva revisioner)
      setTimeout(async () => {
        try {
          const { data: pendingJobs } = await supabase
            .from('worker_queue')
            .select('id')
            .in('status', ['pending','processing'])
            .limit(1);
          const { data: activeRevs } = await supabase
            .from('revisions')
            .select('id')
            .not('status', 'in', '(completed,cancelled)')
            .limit(1);
          const trulyIdle = !(pendingJobs && pendingJobs.length) && !(activeRevs && activeRevs.length);
          if (trulyIdle) {
            this.stopWorkersIfIdle();
          } else {
            // Fortsätt köra och kicka workers för att plocka upp jobb
            await this.kickWorkers();
          }
        } catch (e) {
          console.warn('ensureWorkersRunning idle check error:', e);
        }
      }, 30000);
    } else {
      // Refresh heartbeat so diagnostics can see an active worker
      console.log('🤖 ensureWorkersRunning(): workers already running');
    }
  }

  /**
   * Stoppar workers om inga aktiva jobb finns
   */
  public async stopWorkersIfIdle(): Promise<void> {
    const hasActiveJobs = this.orchestrators.size > 0;
    if (!hasActiveJobs && this.isRunning) {
      console.log('🛑 Inga aktiva jobb – stoppar workers');
      await this.stopWorkers();
    }
  }

  /**
   * Kör ett testscenario med specifika parametrar
   */
  async runTestScenario(scenario: TestScenario): Promise<void> {
    console.log(`Startar testscenario: ${scenario.name}`);
    this.isRunning = true;

    try {
      // Starta workers
      await this.startWorkers();

      // Skapa simultana revisioner
      const revisionPromises = [];
      
      for (let i = 0; i < scenario.revisions; i++) {
        const revisionPromise = this.createTestRevision(
          scenario, 
          i + 1,
          scenario.batches_per_revision,
          scenario.tokens_per_batch
        );
        revisionPromises.push(revisionPromise);
      }

      // Vänta på att alla revisioner startas
      const revisionIds = await Promise.all(revisionPromises);
      console.log(`Startade ${revisionIds.length} testrevisiover:`, revisionIds);

      // Monitorera progress
      await this.monitorProgress(revisionIds);

    } catch (error) {
      console.error('Fel i testscenario:', error);
      throw error;
    } finally {
      await this.stopWorkers();
      this.isRunning = false;
    }
  }

  /**
   * Kör standardtest med 3 revisioner (optimerat för att undvika timeout)
   * Testtid: ~2-5 minuter beroende på AI-responstid
   */
  async runStandardTest(): Promise<string[]> {
    console.log('📋 Starting optimized standard test (ETA: 2-5 minutes)...');
    
    const scenario: TestScenario = {
      name: 'Standard test - 3 revisioner (optimerat)',
      revisions: 3,
      batches_per_revision: 5, // Minska från 10 till 5 batchar för att undvika timeout
      tokens_per_batch: 3000,
      simulate_failures: false,
      cost_limit_test: false
    };

    // Återanvänd aktiva testrevisioner (förhindrar dubbletter vid omstart)
    const { data: existing } = await supabase
      .from('revisions')
      .select('id, company_name, status')
      .like('company_name', 'Test Företag%')
      .not('status', 'in', '(completed,cancelled)')
      .order('started_at', { ascending: false })
      .limit(scenario.revisions);

    const existingIds = (existing || []).map(r => r.id);
    if (existingIds.length >= scenario.revisions) {
      console.log(`♻️ Återanvänder ${existingIds.length} befintliga testrevisioner`, existingIds);
      // Starta workers om inte redan igång
      if (!this.isRunning) {
        this.isRunning = true;
        await this.startWorkers();
      }
      return existingIds.slice(0, scenario.revisions);
    }

    // Starta workers först
    if (!this.isRunning) {
      this.isRunning = true;
      await this.startWorkers();
      console.log('🤖 Workers started and running in background');
    }

    // Skapa endast saknade revisioner SEKVENTIELLT för att undvika timeout
    const toCreate = scenario.revisions - existingIds.length;
    const newIds: string[] = [];
    
    for (let i = 0; i < toCreate; i++) {
      try {
        console.log(`📝 Skapar revision ${existingIds.length + i + 1}/${scenario.revisions}...`);
        
        const revisionId = await this.createTestRevision(
          scenario, 
          existingIds.length + i + 1,
          scenario.batches_per_revision,
          scenario.tokens_per_batch
        );
        
        newIds.push(revisionId);
        console.log(`✅ Revision ${existingIds.length + i + 1} skapad: ${revisionId}`);
        
        // Kort paus mellan revisioner för att undvika överbelastning
        if (i < toCreate - 1) {
          console.log('⏸️ Kort paus mellan revisioner...');
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (error) {
        console.error(`❌ Fel vid skapande av revision ${existingIds.length + i + 1}:`, error);
        throw error;
      }
    }

    const allIds = [...existingIds, ...newIds];
    console.log(`✅ Using ${allIds.length} test revisions (existing: ${existingIds.length}, new: ${newIds.length})`, allIds);
    console.log('⏱️  Processing will continue in background - watch realtime stats!');
    
    return allIds;
  }

  /**
   * Hämtar realtidsstatistik för alla pågående revisioner
   */
  async getRealtimeStats(): Promise<any> {
    console.log('📊 Fetching realtime stats...');
    
    try {
      // Filter out old cancelled revisions (keep only those from last hour)
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      
      const { data: revisions, error } = await supabase
        .from('revisions')
        .select(`
          id, status, total_cost_sek, cost_cap_sek, 
          total_tokens_in, total_tokens_out, started_at, completed_at, updated_at,
          company_name, org_number
        `)
        .or(`status.not.eq.cancelled,started_at.gte.${oneHourAgo}`)
        .order('started_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('❌ Error fetching revisions:', error);
        return { active_revisions: 0, total_cost: 0, total_tokens: 0, revisions: [] };
      }

      console.log('📋 Found revisions:', revisions?.length || 0, revisions?.map(r => `${r.company_name}:${r.status}`) || []);
      
      if (!revisions || revisions.length === 0) {
        return { active_revisions: 0, total_cost: 0, total_tokens: 0, revisions: [] };
      }

    // Check for reports for each revision
    const revisionIds = revisions.map(r => r.id);
    const { data: reports } = await supabase
      .from('revision_reports')
      .select('revision_id')
      .in('revision_id', revisionIds);
    
    const reportsByRevision = new Set(reports?.map(r => r.revision_id) || []);

    const stats = {
      active_revisions: (revisions || []).filter(r => !['completed','cancelled'].includes(r.status)).length,
      total_cost: (revisions || []).reduce((sum, r) => sum + parseFloat(r.total_cost_sek?.toString() || '0'), 0),
      total_tokens: (revisions || []).reduce((sum, r) => sum + Number(r.total_tokens_in || 0) + Number(r.total_tokens_out || 0), 0),
      revisions: [] as any[]
    };

    // Hämta detaljerad progress för varje revision
    for (const revision of revisions) {
      try {
        const orchestrator = new RevisionOrchestrator();
        const status = await orchestrator.getRevisionStatus(revision.id);
        
        const p = status.progress;
        const percentage = p.batches_total > 0 ? Math.round((p.batches_completed / p.batches_total) * 100) : 0;
        stats.revisions.push({
          ...revision,
          has_report: reportsByRevision.has(revision.id),
          progress: { ...p, percentage },
          estimated_completion: status.estimated_completion,
          next_retry_after: status.next_retry_after
        });
      } catch (error) {
        console.warn(`Could not fetch status for revision ${revision.id}:`, error);
        // Lägg till grundläggande info även om detaljerad status saknas
        stats.revisions.push({
          ...revision,
          has_report: reportsByRevision.has(revision.id),
          progress: { batches_completed: 0, batches_total: 10, percentage: 0 },
          estimated_completion: null
        });
      }
    }

      return stats;
    } catch (error) {
      console.error('❌ getRealtimeStats error:', error);
      return { active_revisions: 0, total_cost: 0, total_tokens: 0, revisions: [] };
    }
  }

  /**
   * Genererar dummy-rapport för förhandsgranskning
   */
  async generateDummyReport(revisionId: string): Promise<string> {
    const dummyHtml = `
<!DOCTYPE html>
<html lang="sv">
<head>
    <meta charset="UTF-8">
    <title>Dummy Revisionsrapport</title>
    <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { text-align: center; background: #f0f0f0; padding: 20px; margin-bottom: 30px; }
        .section { margin: 20px 0; padding: 15px; border: 1px solid #ddd; }
        .dummy-content { background: #fff3cd; padding: 10px; border-radius: 5px; margin: 10px 0; }
    </style>
</head>
<body>
    <div class="header">
        <h1>🧪 TESTLAB - Dummy Revisionsrapport</h1>
        <p>Revision ID: ${revisionId}</p>
        <p>Genererad: ${new Date().toLocaleString('sv-SE')}</p>
    </div>

    <div class="section">
        <h2>Executive Summary</h2>
        <div class="dummy-content">
            <p><strong>[SIMULERAT INNEHÅLL]</strong></p>
            <p>Vår granskning av Test Företag AB har identifierat 15 observationer varav 3 är klassificerade som högrisk. 
            Företagets kontrollmiljö bedöms som tillfredsställande med några förbättringsområden inom kassahantering och leverantörsreskontra.</p>
            <p>De mest kritiska fynden rör bristande dokumentation av kontanttransaktioner över 50,000 SEK samt 
            avsaknad av fyrögonskontroll vid betalningar över 100,000 SEK.</p>
        </div>
    </div>

    <div class="section">
        <h2>Modul: Skattekonto</h2>
        <div class="dummy-content">
            <p><strong>[SIMULERAT MODULANALYS]</strong></p>
            <p>• Analyserade transaktioner: 12,456 st</p>
            <p>• Totalt belopp: 15,8 MSEK</p>
            <p>• Identifierade avvikelser: 8 st</p>
            <p>• Riskpoäng: 35/100</p>
            <p>Huvudsakliga fynd inkluderar försenade skattebetalningar och avvikelser i momsinrapportering.</p>
        </div>
    </div>

    <div class="section">
        <h2>Rekommendationer</h2>
        <div class="dummy-content">
            <p><strong>[SIMULERADE REKOMMENDATIONER]</strong></p>
            <ol>
                <li><strong>Hög prioritet:</strong> Implementera fyrögonskontroll för alla betalningar över 100,000 SEK</li>
                <li><strong>Hög prioritet:</strong> Förbättra dokumentation av kontanttransaktioner</li>
                <li><strong>Medium prioritet:</strong> Automatisera avstämning av leverantörsreskontra</li>
                <li><strong>Låg prioritet:</strong> Uppdatera ekonomihandbok med nya rutiner</li>
            </ol>
        </div>
    </div>

    <div class="section">
        <h2>📊 Testlab-statistik</h2>
        <div class="dummy-content">
            <p>• AI-modell: GPT-5-premium (simulerat)</p>
            <p>• Tokens förbrukade: ~50,000 (simulerat)</p>
            <p>• Beräknad kostnad: ~45 SEK (simulerat)</p>
            <p>• Processingstid: ~5 minuter (simulerat)</p>
            <p>• Status: ✅ Testrapport genererad framgångsrikt</p>
        </div>
    </div>

    <div style="text-align: center; margin-top: 40px; padding: 20px; background: #e3f2fd;">
        <p><strong>🧪 Detta är en simulerad rapport från AuditAI Testlab</strong></p>
        <p>I produktionsmiljö skulle denna rapport innehålla verklig AI-genererad analys</p>
    </div>
</body>
</html>`;

    return dummyHtml;
  }

  /**
   * Genererar riktig rapport med GPT-5-premium baserat på findings
   */
  async generateRealReport(revisionId: string): Promise<string> {
    console.log(`🎯 Genererar riktig rapport för revision ${revisionId} med PremiumReportWorker`);
    
    // Importera och använd PremiumReportWorker
    const { PremiumReportWorker } = await import('./PremiumReportWorker');
    const worker = new PremiumReportWorker('report-generator');
    
    try {
      const reportHtml = await worker.generateFinalReport(revisionId);
      console.log(`✅ Riktig rapport genererad för ${revisionId}`);
      return reportHtml;
    } catch (error) {
      console.error(`❌ Fel vid generering av riktig rapport för ${revisionId}:`, error);
      throw error;
    }
  }

  // === PRIVATE METHODS ===

  private async createTestRevision(
    scenario: TestScenario, 
    revisionNumber: number,
    batchCount: number,
    tokensPerBatch: number
  ): Promise<string> {
    
    const orchestrator = new RevisionOrchestrator();
    this.orchestrators.set(`test-revision-${revisionNumber}`, orchestrator);

    // Generera testdata som matchar orchestratorns batchSize (5000 per batch)
    const testData = await this.generateTestData(batchCount * 5000); // 5000 records per batch

    const config = {
      orgNumber: `55${String(revisionNumber).padStart(4, '0')}-${String(1000 + revisionNumber).padStart(4, '0')}`,
      companyName: `Test Företag ${revisionNumber} AB`,
      costCapSek: 200,
      modules: ['skattekonto', 'huvudbok']
    };

    try {
      const revisionId = await orchestrator.startRevision(config, testData);
      console.log(`Testrevision ${revisionNumber} startad med ID: ${revisionId}`);
      return revisionId;
    } catch (error) {
      console.error(`Kunde inte starta testrevision ${revisionNumber}:`, error);
      throw error;
    }
  }

  private async generateTestData(recordCount: number): Promise<any[]> {
    console.log(`🎲 Genererar ${recordCount} testrecords...`);
    
    const testData = [];
    const startDate = new Date('2024-01-01');
    const endDate = new Date('2024-12-31');
    const dateRange = endDate.getTime() - startDate.getTime();

    // Skapa data i mindre chunks för att undvika minnesproblem
    const chunkSize = 1000;
    const totalChunks = Math.ceil(recordCount / chunkSize);
    
    for (let chunk = 0; chunk < totalChunks; chunk++) {
      const chunkStart = chunk * chunkSize;
      const chunkEnd = Math.min(chunkStart + chunkSize, recordCount);
      
      for (let i = chunkStart; i < chunkEnd; i++) {
        const randomDate = new Date(startDate.getTime() + Math.random() * dateRange);

        testData.push({
          datum: randomDate.toISOString().split('T')[0],
          belopp: Math.round((Math.random() * 100000 - 50000) * 100) / 100,
          beskrivning: `Test transaktion ${i + 1}`,
          motpart: `Motpart ${Math.floor(Math.random() * 50) + 1} AB`,
          konto: `${Math.floor(Math.random() * 9000) + 1000}`,
          verifikationsnummer: `V${String(i + 1).padStart(6, '0')}`
        });
      }
      
      // Kort paus mellan chunks för att låta systemet andas
      if (chunk < totalChunks - 1 && recordCount > 5000) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    console.log(`✅ Genererade ${testData.length} testrecords`);
    return testData;
  }

  private async startWorkers(): Promise<void> {
    console.log('Startar test-workers...');
    // Starta batch-workers i bakgrunden (blockera inte)
    this.workers.forEach((worker) => {
      // Fire-and-forget
      worker.start();
    });
    console.log(`${this.workers.length} workers startade`);
  }

  public async kickWorkers(): Promise<void> {
    for (const w of this.workers) {
      try {
        await w.kick();
      } catch (e) {
        console.warn('kickWorkers error:', e);
      }
    }
  }

  private async stopWorkers(): Promise<void> {
    console.log('🛑 Stopping test workers...');
    
    this.workers.forEach(worker => worker.stop());
    this.isRunning = false;
    console.log('✅ Workers stopped');
  }

  /**
   * Kör test med riktig data istället för mock data
   * Begränsat till 2 batcher för kostnadskontroll
   */
  async runRealDataTest(
    scenario: TestScenario, 
    csvData: ParsedCSVData, 
    mapping: Record<string, string>,
    companyConfig?: { companySize: 'small' | 'large'; vatReportingPeriod: 'monthly' | 'quarterly' }
  ): Promise<string[]> {
    console.log('🔥 Starting REAL DATA test with', csvData.validRows, 'records');
    
    // Starta workers
    await this.startWorkers();
    
    // Använd all uppladdad data och låt orkestratorn sköta token-baserad batching
    const limitedData = csvData.rows;
    const actualBatches = makeBatches(limitedData, MINI_MAX_INPUT_TOKENS, EST_TOKENS_PER_ITEM).length;
    
    console.log(`📊 Real data: ${limitedData.length} rader -> ${actualBatches} batchar`);
    
    // Skapa revision med riktig data, fältmappning OCH företagskonfiguration
    const revisionId = await this.createRealDataRevision(limitedData, mapping, companyConfig);
    
    // Övervaka progress
    this.monitorProgress([revisionId]);
    
    return [revisionId];
  }

  /**
   * Skapar en revision med riktig data
   */
  private async createRealDataRevision(
    realData: any[], 
    mapping?: Record<string, string>,
    companyConfig?: { companySize: 'small' | 'large'; vatReportingPeriod: 'monthly' | 'quarterly' }
  ): Promise<string> {
    const orchestrator = new RevisionOrchestrator();
    this.orchestrators.set('real-data-revision', orchestrator);

    const config = {
      orgNumber: '559999-9999',
      companyName: 'Real Data Test',
      costCapSek: 500, // Högre kostnadsttak för riktig data
      modules: ['skattekonto']
    };

    console.log('🚀 Creating real data revision with', realData.length, 'records');
    const revisionId = await orchestrator.startRevision(config, realData, mapping, companyConfig);
    console.log('✅ Real data revision created:', revisionId);
    
    return revisionId;
  }

  /**
   * Stoppar pågående test och alla workers
   */
  async stopTest(): Promise<void> {
    await this.stopWorkers();
  }

  private async monitorProgress(revisionIds: string[]): Promise<void> {
    const maxWaitTime = 30 * 60 * 1000; // 30 minuter max
    const startTime = Date.now();

    while (this.isRunning && (Date.now() - startTime) < maxWaitTime) {
      let allCompleted = true;

      for (const revisionId of revisionIds) {
        try {
          const orchestrator = this.orchestrators.get(`test-revision-${revisionIds.indexOf(revisionId) + 1}`) 
            || this.orchestrators.get('real-data-revision');
          
          if (orchestrator) {
            const status = await orchestrator.getRevisionStatus(revisionId);
            
            console.log(`Revision ${revisionId}: ${status.status} - ` +
              `Batchar: ${status.progress.batches_completed}/${status.progress.batches_total} - ` +
              `Kostnad: ${status.costs.total_cost_sek} SEK`);

            // Om alla batchar är klara men revision inte är completed, trigga rapport-check
            if (status.progress.batches_completed === status.progress.batches_total && 
                status.progress.batches_total > 0 &&
                status.status === 'processing') {
              console.log(`🔍 Alla batchar klara, kontrollerar om rapport kan genereras...`);
              await orchestrator.checkAndTriggerReportIfReady(revisionId);
            }

            if (status.status !== 'completed' && status.status !== 'failed') {
              allCompleted = false;
            }
          }
        } catch (error) {
          console.error(`Fel vid monitoring av revision ${revisionId}:`, error);
        }
      }

      if (allCompleted) {
        console.log('Alla testrevisiover slutförda!');
        break;
      }

      // Vänta 5 sekunder innan nästa kontroll
      await new Promise(resolve => setTimeout(resolve, 5000));
    }
  }
}